import { useMemo, useEffect, useState } from 'react';
import { useGetCampaignFiltersQuery, CampaignFiltersDto } from '@api/apiCompat';

// Helper function to normalize filter data
function normalizeFilters(filters: any[]): CampaignFiltersDto[] {
  if (!filters || !Array.isArray(filters)) return [];
  
  return filters.map((filter: any) => {
    // If it already has the correct structure, return it
    if ('campaign_filters_id' in filter && 'key' in filter && 'type' in filter) {
      return filter as CampaignFiltersDto;
    }
    
    // Otherwise, transform from fallback format
    return {
      campaign_filters_id: filter.filter_id,
      key: filter.filter_name,
      type: filter.filter_type || 'text',
      campaign_type_id_campaign_type: 0,
      price: 0,
      required: filter.required || false,
      placeholder: filter.placeholder || ''
    } as unknown as CampaignFiltersDto;
  });
}

// Mock filter data for fallback
const MOCK_FILTERS = [
  {
    filter_id: 1,
    filter_name: 'Location',
    filter_type: 'text',
    required: true,
    placeholder: 'Enter location'
  },
  {
    filter_id: 2,
    filter_name: 'Keywords',
    filter_type: 'text',
    required: false,
    placeholder: 'Enter search keywords'
  },
  {
    filter_id: 3,
    filter_name: 'Price Range',
    filter_type: 'range',
    required: false,
    placeholder: 'Enter price range'
  }
];

export const useCampaignFiltersWithFallback = (id: string, skip?: boolean) => {
  const [skipQuery, setSkipQuery] = useState(false);

  const { data, isLoading, error } = useGetCampaignFiltersQuery(
    { campaignTypeId: Number(id) || 0 } as any,
    {
      refetchOnMountOrArgChange: true,
      skip: skip || id === null || id === 'undefined' || skipQuery,
      refetchOnFocus: false,
      refetchOnReconnect: false
    },
  );

  useEffect(() => {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as any).status;
      // Stop retrying on 404 or 500 errors
      if (status === 404 || status === 500) {
        setSkipQuery(true);
      }
    }
  }, [error]);

  // Use API data if available, otherwise use fallback
  const normalizedData = useMemo(() => {
    if (data && Array.isArray(data)) {
      return normalizeFilters(data);
    }
    return normalizeFilters(MOCK_FILTERS);
  }, [data]);
  
  const info = useMemo(() => normalizedData ?? [], [normalizedData]);
  return { 
    data: info, 
    isLoading: isLoading && !normalizedData.length,
    error: error && !normalizedData.length ? error : null
  };
};